import {
  getAdminPermissions,
  setLocalAdminPermissions,
} from "@/lib/storeManager";
import { humanizeApiError, toUserFacingError } from "@/lib/humanizeApiError";
import TenantUserRoleService from "@/services/APIs/TenantUserRoleService";
import TenantUserService from "@/services/APIs/TenantUserService";
import type { MyPermission, UserRole } from "@/utils/types/tenantPermissions";
import {
  ADMIN_PERMISSIONS,
  type AdminPermissionKey,
} from "@/project/karuna/constants/adminPermissions";

const EMPTY_LOCATION = {
  latitude: "",
  longitude: "",
  place_id: "",
  name: "",
  display_name: "",
  country: "",
  state: "",
  district: "",
  city: "",
  street: "",
  postal_code: "",
  phone: "",
};

export function isAdminPortalUser(
  user: {
    email?: string;
    phone_number?: string;
    user_type?: string;
  } | null
): boolean {
  if (!user?.email) {
    return false;
  }
  if (user.user_type === "school") {
    return false;
  }
  if (user.user_type === "admin" || user.user_type === "tenant_user") {
    return true;
  }
  // Legacy: email login without phone number
  return !user.phone_number;
}

export function permissionGrantsAccess(permission: MyPermission): boolean {
  if (!permission.is_active) {
    return false;
  }
  return [
    permission.can_read,
    permission.can_select,
    permission.can_create,
    permission.can_write,
    permission.can_delete,
  ].some(Boolean);
}

/** Full access when backend has not configured Karuna permissions yet. */
export function hasLegacyFullAccess(permissions: MyPermission[]): boolean {
  return permissions.length === 0;
}

export function hasAdminPermission(
  permissions: MyPermission[],
  key: AdminPermissionKey
): boolean {
  if (hasLegacyFullAccess(permissions)) {
    return true;
  }
  const match = permissions.find(p => p.name === key);
  return match ? permissionGrantsAccess(match) : false;
}

export function hasAnyAdminPermission(permissions: MyPermission[]): boolean {
  if (hasLegacyFullAccess(permissions)) {
    return true;
  }
  return permissions.some(permissionGrantsAccess);
}

export function canManageAdminUsers(permissions: MyPermission[]): boolean {
  if (hasLegacyFullAccess(permissions)) {
    return true;
  }
  const usersPerm = permissions.find(p => p.name === ADMIN_PERMISSIONS.USERS);
  return usersPerm
    ? [usersPerm.can_create, usersPerm.can_write, usersPerm.can_read].some(
        Boolean
      )
    : false;
}

export async function fetchAndStoreAdminPermissions(): Promise<MyPermission[]> {
  try {
    const permissions = await TenantUserRoleService.getMyPermissions();
    const list = Array.isArray(permissions) ? permissions : [];
    setLocalAdminPermissions(list);
    return list;
  } catch (error) {
    console.warn(
      "Could not fetch admin permissions, using legacy full access:",
      error
    );
    setLocalAdminPermissions([]);
    return [];
  }
}

export function getStoredAdminPermissions(): MyPermission[] {
  return getAdminPermissions();
}

/** Fetch all user↔role mappings via GET /api/tenant-permissions/user-roles/ */
export async function fetchAllUserRoleMappings(): Promise<UserRole[]> {
  const pageSize = 500;
  let page = 1;
  const all: UserRole[] = [];

  while (true) {
    const response = await TenantUserRoleService.listUserRoles(page, pageSize);
    const batch = response.results ?? [];
    all.push(...batch);
    if (!response.next || batch.length < pageSize) {
      break;
    }
    page += 1;
  }

  return all;
}

export function getRoleIdsForUser(
  mappings: UserRole[],
  userId: string
): string[] {
  return mappings.filter(m => m.user === userId).map(m => m.role);
}

/** Assign roles using POST/DELETE on /api/tenant-permissions/user-roles/ */
export async function syncUserRoles(
  userId: string,
  selectedRoleIds: string[],
  mappings: UserRole[]
): Promise<void> {
  const current = mappings.filter(m => m.user === userId);
  const currentRoleIds = new Set(current.map(m => m.role));
  const selected = new Set(selectedRoleIds);

  const toAdd = selectedRoleIds.filter(roleId => !currentRoleIds.has(roleId));
  const toRemove = current.filter(m => !selected.has(m.role));

  await Promise.all([
    ...toAdd.map(roleId =>
      TenantUserRoleService.createUserRole({ user: userId, role: roleId })
    ),
    ...toRemove.map(m => TenantUserRoleService.deleteUserRole(m.id)),
  ]);
}

export function extractCreatedUserId(response: unknown): string | null {
  if (!response || typeof response !== "object") {
    return null;
  }
  const record = response as Record<string, unknown>;
  if (typeof record.id === "string") {
    return record.id;
  }
  if (record.user && typeof record.user === "object") {
    const nested = record.user as Record<string, unknown>;
    if (typeof nested.id === "string") {
      return nested.id;
    }
  }
  return null;
}

export async function resolveUserIdByEmail(
  email: string
): Promise<string | null> {
  const normalized = email.trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  const response = await TenantUserService.listTenantUsers(1, 500);
  const users = response?.users ?? response?.results ?? [];
  const list = Array.isArray(users) ? users : [];
  const match = list.find(
    (user: { id?: string; email?: string }) =>
      user.email?.trim().toLowerCase() === normalized
  );
  return match?.id ?? null;
}

export async function assignSuperAdminRoleToUser(
  userId: string,
  superAdminRoleId: string
): Promise<void> {
  const mappings = await fetchAllUserRoleMappings();
  const existingRoleIds = getRoleIdsForUser(mappings, userId);
  if (existingRoleIds.includes(superAdminRoleId)) {
    return;
  }
  await TenantUserRoleService.createUserRole({
    user: userId,
    role: superAdminRoleId,
  });
}

export async function assignRolesToUser(
  userId: string,
  roleIds: string[]
): Promise<void> {
  if (roleIds.length === 0) {
    return;
  }
  await Promise.all(
    roleIds.map(roleId =>
      TenantUserRoleService.createUserRole({ user: userId, role: roleId })
    )
  );
}

export const SUPER_ADMIN_ROLE_NAME = "Super Admin";

export function findSuperAdminRole(
  roles: { id: string; name: string; is_active?: boolean }[]
): { id: string; name: string } | undefined {
  return roles.find(
    role =>
      role.is_active !== false &&
      role.name.trim().toLowerCase() === SUPER_ADMIN_ROLE_NAME.toLowerCase()
  );
}

export function userHasSuperAdminRole(
  roleIds: string[],
  superAdminRoleId: string
): boolean {
  return roleIds.includes(superAdminRoleId);
}

export async function removeAllUserRoleMappings(
  userId: string,
  mappings: UserRole[]
): Promise<void> {
  const userMappings = mappings.filter(m => m.user === userId);
  await Promise.all(
    userMappings.map(m => TenantUserRoleService.deleteUserRole(m.id))
  );
}

export async function deleteSuperAdminUser(
  userId: string,
  mappings: UserRole[]
): Promise<void> {
  await removeAllUserRoleMappings(userId, mappings);
  await TenantUserService.deleteUser(userId);
}

export const KARUNA_USER_TYPES = {
  ADMIN: "admin",
  SCHOOL: "school",
  TENANT_USER: "tenant_user",
} as const;

export function isKarunaAdminUser(user: { user_type?: string }): boolean {
  return user.user_type === KARUNA_USER_TYPES.ADMIN;
}

/** Roles intended for the Karuna admin portal (not school accounts). */
export function isKarunaAdminRole(role: {
  name?: string;
  description?: string;
}): boolean {
  const name = (role.name ?? "").toLowerCase();
  const description = (role.description ?? "").toLowerCase();
  return (
    name.includes("admin") ||
    description.includes("admin portal") ||
    description.includes("karuna admin")
  );
}

export function buildAdminUserPayload(options: {
  email: string;
  password: string;
  firstName: string;
  lastName?: string;
}): Record<string, unknown> {
  const email = options.email.trim();
  const firstName = options.firstName.trim();
  const lastName = (options.lastName ?? "").trim();

  return {
    email,
    password: options.password,
    first_name: firstName,
    last_name: lastName,
    full_name: [firstName, lastName].filter(Boolean).join(" "),
    username: email.split("@")[0] || email,
    // Backend accepts tenant_user — Super Admin access comes from role assignment
    user_type: KARUNA_USER_TYPES.TENANT_USER,
    is_active: true,
    profile_picture: "",
    phone_number: "",
    mobile_number: "",
    location: { ...EMPTY_LOCATION },
  };
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  const err = error as {
    response?: { data?: Record<string, unknown> };
    message?: string;
  };
  const data = err?.response?.data;
  if (!data) {
    return toUserFacingError(err?.message ?? "", fallback);
  }

  // Some endpoints nest the message: { error: { code, message } }.
  const nestedErrorMessage =
    data.error && typeof data.error === "object"
      ? (data.error as { message?: unknown }).message
      : undefined;

  // Intercept known raw server/database errors (covers detail/error/message).
  const rawCandidates = [
    data.detail,
    data.error,
    data.message,
    nestedErrorMessage,
  ].filter((value): value is string => typeof value === "string");
  for (const raw of rawCandidates) {
    const friendly = humanizeApiError(raw);
    if (friendly) {
      return friendly;
    }
  }

  const errors = data.errors;
  if (errors && typeof errors === "object") {
    const parts = Object.entries(errors as Record<string, unknown>).flatMap(
      ([field, value]) => {
        if (Array.isArray(value)) {
          return value.map(msg => `${field}: ${String(msg)}`);
        }
        return [`${field}: ${String(value)}`];
      }
    );
    if (parts.length > 0) {
      return parts.join(". ");
    }
  }

  // Sanitize whatever raw message remains so technical errors never reach users.
  const best =
    (typeof data.message === "string" && data.message) ||
    (typeof data.detail === "string" && data.detail) ||
    (typeof data.error === "string" && data.error) ||
    (typeof nestedErrorMessage === "string" && nestedErrorMessage) ||
    "";
  return toUserFacingError(best, fallback);
}
